import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../static/constants'
import { MultiDatePicker } from 'components/molecules/MultiDatePicker'
import moment from 'moment'
import { ISelectedGuests } from 'utils/reservation'
import { MinGuestsCounter } from 'components/molecules/MinGuestsCounter'
import { IFeature } from 'static/interfaces'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { utilService } from 'core/services'
import { SearchAreaCard } from './SearchAreaCard'
import Classnames from 'classnames'
import { useSearchFilters } from 'utils/useSearchFilters'
import { getSearchFiltersCookie, updateSearchFiltersCookie } from 'utils/searchFiltersCookie'
import { resolveInitialSearchAreaFeature } from 'utils/searchAreaFeature'
import SvgIcon from 'components/atoms/SvgIcon'
export interface ISearchProps {
  areaFeatures: IJsonResponse
  isUnderTablet?: boolean
  isScrolled?: boolean
  currentFeature?: { slug: string; feature_type: string } | null
}

export const HeaderSearchPanel: React.VFC<ISearchProps> = ({
  areaFeatures,
  isUnderTablet,
  isScrolled,
  currentFeature,
}) => {
  const { data: parsedAreaFeatures } = utilService.getDataFromJson(areaFeatures)
  const searchFiltersCookie = getSearchFiltersCookie()

  const urlParams = new URLSearchParams(window.location.search)
  const initialFeature = resolveInitialSearchAreaFeature({
    currentFeatureSlug: currentFeature?.slug ?? null,
    currentFeatureType: currentFeature?.feature_type ?? null,
    pathname: window.location.pathname,
    parsedAreaFeatures,
    searchFiltersAreaFeature: searchFiltersCookie?.areaFeature ?? null,
  })
  const initialDate = searchFiltersCookie?.date ?? urlParams.get('date') ?? null
  const initialGuests =
    searchFiltersCookie?.guests ??
    (() => {
      const guestCountsParam = urlParams.get('guest_counts')
      if (guestCountsParam) {
        try {
          const [adults, children, infants] = JSON.parse(guestCountsParam)
          return {
            adults: adults ?? constants.DEFAULT_SEARCH_FILTER_GUESTS.adults,
            children: children ?? constants.DEFAULT_SEARCH_FILTER_GUESTS.children,
            infants: infants ?? constants.DEFAULT_SEARCH_FILTER_GUESTS.infants,
          }
        } catch {
          return constants.DEFAULT_SEARCH_FILTER_GUESTS
        }
      }

      return constants.DEFAULT_SEARCH_FILTER_GUESTS
    })()

  const [sending, setSending] = React.useState(false)
  const [areaFeature, setAreaFeature] = React.useState<IFeature | null>(initialFeature ?? null)
  const [date, setDate] = React.useState<moment.Moment | null>(
    initialDate ? moment(initialDate) : null
  )
  const [guests, setGuests] = React.useState<ISelectedGuests>({
    adults: initialGuests.adults,
    children: initialGuests.children,
    infants: initialGuests.infants,
  })

  React.useEffect(() => {
    const cookieAreaSlug = searchFiltersCookie?.areaFeature?.slug ?? null
    const resolvedAreaFeature = initialFeature
      ? { id: initialFeature.id, slug: initialFeature.slug, title: initialFeature.title }
      : null
    const resolvedAreaSlug = resolvedAreaFeature?.slug ?? null

    if (cookieAreaSlug === resolvedAreaSlug) {
      return
    }

    updateSearchFiltersCookie({
      areaFeature: resolvedAreaFeature,
    })
  }, [initialFeature, searchFiltersCookie?.areaFeature?.slug])

  const [showingPanel, setShowingPanel] = React.useState<'where' | 'dates' | 'guests' | 'close'>(
    'close'
  )
  const dateLabel = React.useMemo(() => {
    if (date) {
      return `${date?.format('MMM DD')}`
    }
    return 'ADD DATE'
  }, [date])

  const setIsOpenWhere = (isOpen: boolean) => {
    setShowingPanel(isOpen ? 'where' : 'close')
  }
  const setIsOpenDatePicker = (isOpen: boolean) => {
    setShowingPanel(isOpen ? 'dates' : 'close')
  }
  const setIsOpenGuestCount = (isOpen: boolean) => {
    setShowingPanel(isOpen ? 'guests' : 'close')
  }
  const { fireSearchEvent, saveSearchFilters, searchHref } = useSearchFilters({
    date,
    guests,
    areaFeature,
  })
  const didMountRef = React.useRef(false)

  React.useEffect(() => {
    // 初回レンダリング時は自動発火を防ぐ
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    // saveSearchFiltersは、date, guests, areaFeatureのいずれかが変更された際に更新される
    // それをトリガーにしてイベントを発火
    saveSearchFilters()
  }, [saveSearchFilters])

  const search = React.useCallback(async () => {
    // 送信完了時に遷移する
    try {
      setSending(true)
      await fireSearchEvent(() => {
        location.href = searchHref
      })
    } finally {
      setSending(false)
    }
  }, [searchHref, fireSearchEvent])

  return (
    <Search className={Classnames({ scrolled: isScrolled })}>
      <SearchSection className={Classnames('where', { scrolled: isScrolled })}>
        <div className="SearchSection_Label">WHERE</div>
        <div
          className="toggle"
          onClick={() => setIsOpenWhere(showingPanel === 'where' ? false : true)}
        >
          {areaFeature ? (
            <span className="filterValue">{areaFeature.title}</span>
          ) : (
            <span className="placeholder">FIND PLACE</span>
          )}
          {areaFeature ? (
            <SvgIcon
              name="close"
              className="clearFilterValue"
              onClick={e => {
                e.stopPropagation()
                setAreaFeature(null)
              }}
            />
          ) : (
            <SvgIcon name="keyboard_arrow_down" className="icon" />
          )}
        </div>
        {showingPanel === 'where' && (
          <TogglePanel>
            <div className={Classnames('wherePanel', { small: isUnderTablet })}>
              {parsedAreaFeatures.map(feature => (
                <SearchAreaCard
                  key={feature.id}
                  areaFeature={feature}
                  setAreaFeature={selectedFeature => {
                    setIsOpenWhere(false)
                    setAreaFeature(selectedFeature)
                  }}
                />
              ))}
            </div>
          </TogglePanel>
        )}
      </SearchSection>
      <SearchSection className={Classnames('dates', { scrolled: isScrolled })}>
        <div className="SearchSection_Label">DATES</div>
        <div
          className="toggle"
          onClick={() => setIsOpenDatePicker(showingPanel === 'dates' ? false : true)}
        >
          {date ? (
            <span className="filterValue">{dateLabel}</span>
          ) : (
            <span className="placeholder">ADD DATE</span>
          )}
          <SvgIcon name="keyboard_arrow_down" className="icon" />
        </div>
        {showingPanel === 'dates' && (
          <TogglePanel>
            <MultiDatePicker
              forSingleDate={true}
              selectedModalDate={date ?? moment()}
              dates={date ? [date] : []}
              setDates={(dates: moment.Moment[]) => {
                if (dates.length > 0) {
                  setDate(dates[0])
                  setIsOpenDatePicker(false)
                } else {
                  setDate(null)
                }
              }}
              setIsOpenDatePicker={setIsOpenDatePicker}
            />
          </TogglePanel>
        )}
      </SearchSection>
      <SearchSection className={Classnames('guests', { scrolled: isScrolled })}>
        <div className="SearchSection_Label">GUESTS</div>
        <div
          className="toggle"
          onClick={() => setIsOpenGuestCount(showingPanel === 'guests' ? false : true)}
        >
          <span className="filterValue">
            {guests.adults + guests.children + guests.infants} guests
          </span>
          <SvgIcon name="keyboard_arrow_down" className="icon" />
        </div>
        {showingPanel === 'guests' && (
          <TogglePanel>
            <MinGuestsCounter
              isPrivate={false}
              guests={guests}
              setGuests={setGuests}
              setIsOpenGuestCount={setIsOpenGuestCount}
            />
          </TogglePanel>
        )}
      </SearchSection>
      <button className="searchButton" onClick={search} disabled={sending}>
        Search
      </button>
    </Search>
  )
}

export const Search = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 68px;
  padding: 12px 16px 12px 26px;
  gap: 28px;
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 58px;

  .searchButton {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 18px;
    height: 44px;

    background: ${constants.COLORS.info3};
    border-radius: 50px;
    outline: none;
    border: none;
    color: ${constants.COLORS.white};
    transition: background-color 0.2s;
    cursor: pointer;
    &:hover {
      background-color: ${constants.COLORS.info4};
    }
    &:disabled {
      background-color: ${constants.COLORS.disabled};
      cursor: not-allowed;
    }
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      padding: 5 16px;
      height: 34px;
    }
  }

  &.scrolled {
    height: 54px;
    .searchButton {
      padding: 5 16px;
      height: 34px;
    }
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
    height: 54px;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    padding: 10px 16px 10px 20px;
    gap: 15px;
  }
`

export const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px;
  gap: 4px;
  width: 100%;
  .SearchSection_Label {
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    color: ${constants.COLORS.black};
  }

  &.where {
    max-width: 144px;
  }
  &.dates {
    max-width: 134px;
  }
  &.where,
  &.dates {
    min-width: 110px;
  }
  &.guests {
    max-width: 110px;
    min-width: 68x;
  }
  .toggle {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0px;

    width: 100%;
    height: 24px;
    font-size: 14px;
    cursor: pointer;
    .icon {
      width: 24px;
      height: 24px;
    }
    .placeholder {
      color: ${constants.COLORS.divider};
    }
    &:hover svg {
      color: ${constants.COLORS.primary};
    }
  }
  .clearFilterValue {
    color: ${constants.COLORS.divider};
    cursor: pointer;
    &:hover {
      color: ${constants.COLORS.primary};
    }
    width: 24px;
    height: 24px;
  }

  &.scrolled {
    .SearchSection_Label {
      display: none;
    }
    .toggleArrow {
      display: none;
    }
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
    .SearchSection_Label {
      display: none;
    }
    .toggleArrow {
      display: none;
    }
  }
`

export const TogglePanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  .wherePanel {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    width: max-content;
    gap: 20px;
    border-radius: 12px;
    background-color: ${constants.COLORS.white};
    box-shadow: ${constants.COLORS.cardShadow};
    padding: 24px;
    &.small {
      grid-template-columns: repeat(2, 1fr);
    }
    > div {
      cursor: pointer;
      &:hover {
        .whereLabel {
          color: ${constants.COLORS.primary};
        }
      }
    }
  }
  .whereCard {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    width: 150px;
    height: 50px;
    .whereIcon {
      height: 50px;
      width: 50px;
      border-radius: 50%;
    }
  }
`
